import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import NotificationBell from '@/components/NotificationBell';
import { 
  Truck, 
  LayoutDashboard, 
  Package, 
  FileText, 
  BarChart3, 
  Users, 
  MapPin, 
  LogOut,
  Menu,
  X,
  CreditCard,
  Settings,
  MessageCircle,
  Store,
  HandCoins,
  BadgePercent,
  AlertTriangle
} from 'lucide-react';
import { useState } from 'react';

const Sidebar = ({ role }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = {
    distributor: [
      { to: '/distributor', icon: LayoutDashboard, label: 'Dashboard', end: true },
      { to: '/distributor/shipments', icon: Package, label: 'Shipments' },
      { to: '/distributor/analytics', icon: BarChart3, label: 'Analytics' },
      { to: '/bol', icon: FileText, label: 'Bill of Lading' },
      { to: '/payments', icon: CreditCard, label: 'Payments' },
      { to: '/chat', icon: MessageCircle, label: 'Chat' },
      { to: '/marketplace', icon: Store, label: 'Marketplace' },
      { to: '/settings', icon: Settings, label: 'Settings' },
    ],
    driver: [
      { to: '/driver', icon: LayoutDashboard, label: 'Dashboard', end: true },
      { to: '/driver/loads', icon: Package, label: 'My Loads' },
      { to: '/bol', icon: FileText, label: 'Documents' },
      { to: '/wallet', icon: HandCoins, label: 'Wallet' },
      { to: '/payments', icon: CreditCard, label: 'Payments' },
      { to: '/chat', icon: MessageCircle, label: 'Chat' },
      { to: '/marketplace', icon: Store, label: 'Marketplace' },
      { to: '/settings', icon: Settings, label: 'Settings' },
    ],
    dispatcher: [
      { to: '/dispatcher', icon: LayoutDashboard, label: 'Command Center', end: true },
      { to: '/dispatcher/drivers', icon: Users, label: 'Fleet' },
      { to: '/dispatcher/shipments', icon: Package, label: 'Shipments' },
      { to: '/bol', icon: FileText, label: 'Bill of Lading' },
      { to: '/payments', icon: CreditCard, label: 'Payments' },
      { to: '/chat', icon: MessageCircle, label: 'Chat' },
      { to: '/marketplace', icon: Store, label: 'Marketplace' },
      { to: '/settings', icon: Settings, label: 'Settings' },
    ],
    admin: [
      { to: '/admin', icon: LayoutDashboard, label: 'Admin Panel', end: true },
      { to: '/admin/chat-control', icon: Users, label: 'Chat Admin' },
      { to: '/payments', icon: CreditCard, label: 'Payments' },
      { to: '/chat', icon: MessageCircle, label: 'Chat' },
      { to: '/bol', icon: FileText, label: 'Bill of Lading' },
      { to: '/settings', icon: Settings, label: 'Settings' },
    ],
    sales_rep: [
      { to: '/sales-rep', icon: LayoutDashboard, label: 'Sales Dashboard', end: true },
      { to: '/wallet', icon: HandCoins, label: 'Wallet' },
      { to: '/chat', icon: MessageCircle, label: 'Chat' },
      { to: '/settings', icon: Settings, label: 'Settings' },
      { to: '/careers', icon: BadgePercent, label: 'Careers' },
    ],
    emergency: [
      { to: '/emergency', icon: LayoutDashboard, label: 'Emergency Dashboard', end: true },
      { to: '/chat', icon: MessageCircle, label: 'Chat' },
      { to: '/settings', icon: Settings, label: 'Settings' },
      { to: '/emergency-services', icon: AlertTriangle, label: 'Emergency Services' },
    ],
  };

  const items = navItems[role] || [];

  return (
    <>
      {/* Mobile Toggle */}
      <Button
        variant="outline"
        size="sm"
        className="fixed top-3 left-3 z-[70] lg:hidden bg-card/95 border-border shadow-lg"
        onClick={() => setMobileOpen(!mobileOpen)}
        data-testid="mobile-menu-toggle"
      >
        {mobileOpen ? <X className="w-4 h-4 mr-2" /> : <Menu className="w-4 h-4 mr-2" />}
        Menu
      </Button>

      {/* Overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed left-0 top-0 h-full w-[82vw] max-w-72 bg-card border-r border-border z-[75] transition-transform duration-300",
        "lg:translate-x-0",
        mobileOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <img
                src="/assets/eagle-favco.png"
                alt="The Eagle logo"
                className="w-10 h-10 rounded-lg object-cover border border-white/20"
              />
              <div>
                <span className="text-xl font-heading font-bold tracking-tight block">THE EAGLE</span>
                <span className="text-xs text-muted-foreground uppercase tracking-widest">{role}</span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) => cn(
                  "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
                  "hover:bg-muted/50",
                  isActive 
                    ? "bg-primary/10 text-primary border-l-4 border-l-primary" 
                    : "text-muted-foreground hover:text-foreground"
                )}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* User Info & Logout */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center justify-between mb-4 px-4">
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user?.name}</p>
                <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
              </div>
              <NotificationBell />
            </div>
            <Button
              variant="outline"
              className="w-full justify-start gap-3 border-destructive/30 text-destructive hover:bg-destructive/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </Button>
            <div className="mt-4 pt-3 border-t border-border/60 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground px-1">
              <NavLink to="/about" className="hover:text-foreground">About</NavLink>
              <NavLink to="/investors" className="hover:text-foreground">Investors</NavLink>
              <NavLink to="/terms" className="hover:text-foreground">Terms</NavLink>
              <NavLink to="/privacy" className="hover:text-foreground">Privacy</NavLink>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
